import { effectiveSpeed } from "../movement/speed.ts";
import { EffectLimit, directionUnder } from "../combat/effects.ts";
import { BLOCKING, SWING_HOLD_INPUTS, heldFacing, stepWalk, type CollisionGrid, type Direction, type Facing, type MovementState } from "../movement/step.ts";
import type { Offsets } from "../movement/true-point.ts";

/**
 * Local player prediction (CLIENT.md sections 2 and 3, ADR-032): every input is applied with the shared
 * step and kept until the server acknowledges it; an ack resets to the server's state and replays the
 * rest. The error between the replayed and the previously predicted position is classified into a
 * reconciliation band; the renderer smooths it, the simulation state itself is always exact. Every step, predicted and
 * replayed, runs under the movement limits the server last confirmed (ADR-079 decision 4), so a stunned character's page
 * predicts no movement once the stun arrives. An act's turn holds the facing for the `SWING_HOLD_INPUTS` inputs after
 * the input it follows, as the map server holds it for an act used (ADR-073's amendment "the way a displacement goes,
 * and a swing keeps its facing"), so a swing plays facing its aim while the character walks on and no reconcile turns it
 * back. A chain's turn also roots the character over the inputs after it (ADR-073's amendment "the Strike combo and the
 * Heavy Strike chain", item 8): they neither step nor turn, as the map server drains them under the `Move` limit while
 * the chain runs, replayed so until acknowledged, dropped with the turn on a refusal and cut short when the chain ends.
 * A step is `stepWalk` at the walk's speed or, where the page predicted a run step, the run's (ADR-116 decision 12):
 * it moves the character's true point and the lattice point follows (ADR-116's amendment of 2026-10-07). The predictor
 * carries the true point's offsets as the map server does, and every acknowledged state brings the map server's own, so
 * a replay from the server's state steps exactly as the map server will from there.
 */

/**
 * Most inputs held before the connection counts as degraded.
 */
export const PENDING_CAP = 64;

/**
 * Reconciliation bands, thresholds in sixteenths (ADR-032 item 3).
 */
export const Band = { Ignore: 0, Soft: 1, Firm: 2, Snap: 3 } as const;
export type Band = (typeof Band)[keyof typeof Band];

const SOFT_FROM = 2;
const FIRM_FROM = 8;
const SNAP_FROM = 80;

/**
 * Classifies a position error.
 *
 * @param error Distance in sixteenths (Chebyshev, the larger axis).
 * @returns The band.
 */
export function classifyError(error: number): Band {
  if (error >= SNAP_FROM) {
    return Band.Snap;
  }
  if (error >= FIRM_FROM) {
    return Band.Firm;
  }
  if (error >= SOFT_FROM) {
    return Band.Soft;
  }
  return Band.Ignore;
}

/**
 * An input the client sent and still waits on, with whether the page predicted it as a run step, which a reconcile
 * replays at the run's speed (ADR-116 decision 12), and, on the input that carried it, the jump bit (decision 20): a
 * hop moves nothing today, so nothing replays it; the bit is kept where a later jump that moves would be predicted.
 */
export interface PendingInput {
  readonly seq: number;
  readonly dir: Direction;
  readonly ran: boolean;
  readonly jump?: true;
}

/**
 * A predicted turn: the facing an act of an ability sent after input `after` gives its caster, the last input seq
 * whose step keeps that facing, and the last one its chain roots, `after` itself for none.
 */
interface PendingTurn {
  readonly ability: number;
  readonly after: number;
  readonly facing: Facing;
  readonly heldThrough: number;
  readonly rootedThrough: number;
}

/**
 * What a reconcile reports to the renderer.
 */
export interface Reconciliation {
  readonly error: number;
  readonly band: Band;
}

/**
 * The local player's predictor.
 */
export class Predictor {
  private state: MovementState;
  private readonly queue: PendingInput[] = [];
  private readonly turns: PendingTurn[] = [];
  private nextSeq = 1;
  private lastAck = 0;
  private degraded_ = false;
  private limits_ = 0;
  private moveSpeed_ = 0;
  private heldThrough = 0;
  private rootedThrough = 0;
  private readonly carried: Offsets = { x: 0, y: 0 };

  /**
   * @param start The state `welcome` gave, in sixteenths.
   * @param grid The client's collision grid.
   * @param walkSpeed The rules' `walk_speed`, in speed units.
   * @param runSpeed The rules' `run_speed`, in speed units, the length of an input that ran.
   * @param firstSeq The seq of the first input, 1 unless given: a page that changes map on one socket goes on from the
   *   last seq it sent, since inputs the old map's predictor sent may still reach the new map server first and raise the
   *   lowest seq it still accepts above the page's next.
   */
  public constructor(
    start: MovementState,
    private grid: CollisionGrid,
    private readonly walkSpeed: number,
    private readonly runSpeed: number,
    firstSeq = 1,
  ) {
    this.state = start;
    this.nextSeq = firstSeq;
  }

  /**
   * The offsets of the predicted true point from the predicted lattice point (`Offsets`): what every pending input's
   * step has left, from the offsets the map server last acknowledged. The page draws its character with them.
   *
   * @returns The offsets, the predictor's own object: read, never written.
   */
  public get offsets(): Readonly<Offsets> {
    return this.carried;
  }

  /**
   * The predicted state after every pending input.
   *
   * @returns The state.
   */
  public get predicted(): MovementState {
    return this.state;
  }

  /**
   * Inputs sent and not yet acknowledged.
   *
   * @returns The count.
   */
  public get pending(): number {
    return this.queue.length;
  }

  /**
   * The seq of the last input made, `firstSeq - 1` before any: what an `act` sent now names as its `afterSeq`.
   *
   * @returns The seq.
   */
  public get lastSeq(): number {
    return this.nextSeq - 1;
  }

  /**
   * Whether the pending buffer overflowed since the last reconcile; the next one hard-snaps.
   *
   * @returns True when degraded.
   */
  public get degraded(): boolean {
    return this.degraded_;
  }

  /**
   * The movement limits the steps run under.
   *
   * @returns The `EffectLimit` bits, 0 for none.
   */
  public get limits(): number {
    return this.limits_;
  }

  /**
   * Sets the movement limits the server confirmed for the character, from its owner's `effects` frame: later inputs
   * and every replay step under them until they change.
   *
   * @param limits The `EffectLimit` bits in force, 0 for none.
   */
  public setLimits(limits: number): void {
    this.limits_ = limits;
  }

  /**
   * The move speed the steps run at, the sum of the confirmed effects' `moveSpeed` in whole percents.
   *
   * @returns The percent, 0 for none.
   */
  public get moveSpeed(): number {
    return this.moveSpeed_;
  }

  /**
   * Sets the move speed the server confirmed for the character, from its owner's `effects` frame
   * (`moveSpeedOfEffects`, ADR-144's amendment of 2026-11-01): later inputs and every replay step take it until it
   * changes, so a Drenched character's page steps at the speed the map server does.
   *
   * @param percent The sum of the effects' `moveSpeed`, 0 for none.
   */
  public setMoveSpeed(percent: number): void {
    this.moveSpeed_ = percent;
  }

  /**
   * Applies an intent locally and records it for the wire.
   *
   * @param dir The intent.
   * @param ran Whether the input steps at the run's length: what `mayRun` answers for the flags the page sends with it
   *   and the state the server last told it; false when not given.
   * @param jump Whether the input carries the jump bit; false when not given. It changes nothing of the step.
   * @returns The seq to send with it.
   */
  public input(dir: Direction, ran = false, jump = false): PendingInput {
    const seq = this.nextSeq;
    this.nextSeq += 1;
    this.state = this.step(this.state, dir, seq, this.heldThrough, this.rootedThrough, ran);
    const input: PendingInput = jump ? { seq, dir, ran, jump } : { seq, dir, ran };
    if (this.queue.length >= PENDING_CAP) {
      this.queue.length = 0;
      this.degraded_ = true;
      return input;
    }
    this.queue.push(input);
    return input;
  }

  /**
   * Turns the predicted character to a facing at this point of the input sequence, as an act sent now turns it on
   * the map server once the input it follows has applied (ADR-073 decisions 3 and 15). The turn is replayed after
   * that input on each reconcile until the server acknowledges the input after it, whose state then holds the act's
   * outcome: the turn for an act used, none for one refused.
   *
   * The `SWING_HOLD_INPUTS` inputs after it keep the facing, replayed so until the server has acknowledged the last of
   * them, unless `release` drops the turn. A chain's `rootInputs` inputs after it neither step nor turn, replayed so
   * until acknowledged, unless `release` drops the turn or `endRoot` cuts the root short.
   *
   * @param facing The facing the act's aim gives (`facingOfAim`).
   * @param ability The act's ability number, which a refusal names.
   * @param rootInputs The inputs after it the act's chain roots, 0 for an act with no chain.
   * @returns The seq of the last input made, which the act names as its `afterSeq`.
   */
  public turn(facing: Facing, ability: number, rootInputs = 0): number {
    const after = this.lastSeq;
    this.state = { x: this.state.x, y: this.state.y, facing };
    const rootedThrough = after + rootInputs;
    this.heldThrough = after + SWING_HOLD_INPUTS;
    this.rootedThrough = Math.max(this.rootedThrough, rootedThrough);
    this.turns.push({ ability, after, facing, heldThrough: this.heldThrough, rootedThrough });
    return after;
  }

  /**
   * Ends every root at the last input made, as a stun or a death ends a chain on the map server: the inputs already made
   * stay rooted on every replay, and the ones made after walk.
   */
  public endRoot(): void {
    const last = this.lastSeq;
    for (let index = 0; index < this.turns.length; index += 1) {
      const turn = this.turns[index] as PendingTurn;
      if (turn.rootedThrough > last) {
        this.turns[index] = { ...turn, rootedThrough: last };
      }
    }
    this.rootedThrough = Math.min(this.rootedThrough, last);
  }

  /**
   * Drops the newest turn of an ability and its hold, for an act of it the server refused, which turns and holds
   * nothing on the map server; a turn of another ability made since is kept. The inputs after the dropped turn turn the
   * character again as their steps give, from the next input and on every replay. Nothing happens when no turn of the
   * ability is held.
   *
   * @param ability The refused act's ability number.
   */
  public release(ability: number): void {
    for (let index = this.turns.length - 1; index >= 0; index -= 1) {
      if ((this.turns[index] as PendingTurn).ability === ability) {
        this.turns.splice(index, 1);
        this.heldThrough = this.turns.reduce((held, turn) => Math.max(held, turn.heldThrough), 0);
        this.rootedThrough = this.turns.reduce((rooted, turn) => Math.max(rooted, turn.rootedThrough), 0);
        return;
      }
    }
  }

  /**
   * One input's step from a state under the confirmed limits, keeping the facing before it while a swing holds it, and
   * neither stepping nor turning while a chain roots it.
   *
   * @param from The state before the input.
   * @param dir The input's intent.
   * @param seq The input's seq.
   * @param heldThrough The last seq a swing holds, 0 for none.
   * @param rootedThrough The last seq a chain roots, 0 for none.
   * @param ran Whether the input steps at the run's speed.
   * @returns The state after the input.
   */
  private step(from: MovementState, dir: Direction, seq: number, heldThrough: number, rootedThrough: number, ran: boolean): MovementState {
    const limits = seq <= rootedThrough ? this.limits_ | EffectLimit.Move : this.limits_;
    const stepped = stepWalk(from, directionUnder(limits, dir), this.grid, BLOCKING, effectiveSpeed(ran ? this.runSpeed : this.walkSpeed, 0, this.moveSpeed_), this.carried);
    const facing = heldFacing(from.facing, stepped.facing, seq, heldThrough);
    return facing === stepped.facing ? stepped : { x: stepped.x, y: stepped.y, facing };
  }

  /**
   * Moves the prediction into another map's frame, as a crossing's `welcome` does (ADR-147 decisions 6 and 18): the
   * predicted state shifts by the difference between the two frames, later steps read the new map's grid, and the pending
   * inputs, turns, offsets and seq stay as they are, so the new map's snapshots reconcile the inputs sent before the
   * crossing as they would any others.
   *
   * @param grid The new map's collision grid.
   * @param dx What to add across, in sixteenths: the map left's place minus the new map's, in tiles, times 16.
   * @param dy The same down.
   */
  public rebase(grid: CollisionGrid, dx: number, dy: number): void {
    this.grid = grid;
    this.state = { x: this.state.x + dx, y: this.state.y + dy, facing: this.state.facing };
  }

  /**
   * Applies the server's state for an acknowledged seq: drops inputs up to it, replays the rest, and
   * reports how far the prediction was off. A turn made after an input still pending, or after the acknowledged input
   * itself, is replayed after it: an act sent a frame after its input may reach the map server the tick after that input
   * applied and resolve then, so the state acknowledging its input need not hold its turn yet. A turn whose following
   * input the server has acknowledged is in its state, and its hold is still replayed over the inputs after it until the
   * server has acknowledged the last input it holds.
   *
   * @param ackSeq The seq the server has applied.
   * @param server The server's state for the local player at that seq.
   * @param offsets The offsets of the character's true point at that seq, as the snapshot carried them: the replay
   *   starts from them, so the prediction's steps are the map server's own. The error is read from the lattice points
   *   alone.
   * @returns The error and band, or null when the ack is stale or unknown.
   */
  public reconcile(ackSeq: number, server: MovementState, offsets: Readonly<Offsets>): Reconciliation | null {
    if (ackSeq <= this.lastAck || ackSeq >= this.nextSeq) {
      return null;
    }
    this.lastAck = ackSeq;
    this.carried.x = offsets.x;
    this.carried.y = offsets.y;
    if (this.degraded_) {
      this.degraded_ = false;
      this.queue.length = 0;
      this.turns.length = 0;
      this.heldThrough = 0;
      this.rootedThrough = 0;
      this.state = server;
      return { error: 0, band: Band.Snap };
    }
    let drop = 0;
    while (drop < this.queue.length && (this.queue[drop] as PendingInput).seq <= ackSeq) {
      drop += 1;
    }
    this.queue.splice(0, drop);
    let kept = 0;
    for (const turn of this.turns) {
      if (Math.max(turn.heldThrough, turn.rootedThrough) > ackSeq) {
        this.turns[kept] = turn;
        kept += 1;
      }
    }
    this.turns.length = kept;
    let replayed = server;
    let held = 0;
    let rooted = 0;
    let next = 0;
    while (next < this.turns.length && (this.turns[next] as PendingTurn).after < ackSeq) {
      held = Math.max(held, (this.turns[next] as PendingTurn).heldThrough);
      rooted = Math.max(rooted, (this.turns[next] as PendingTurn).rootedThrough);
      next += 1;
    }
    for (const input of this.queue) {
      while (next < this.turns.length && (this.turns[next] as PendingTurn).after < input.seq) {
        const turn = this.turns[next] as PendingTurn;
        replayed = { x: replayed.x, y: replayed.y, facing: turn.facing };
        held = Math.max(held, turn.heldThrough);
        rooted = Math.max(rooted, turn.rootedThrough);
        next += 1;
      }
      replayed = this.step(replayed, input.dir, input.seq, held, rooted, input.ran);
    }
    for (; next < this.turns.length; next += 1) {
      const turn = this.turns[next] as PendingTurn;
      replayed = { x: replayed.x, y: replayed.y, facing: turn.facing };
      held = Math.max(held, turn.heldThrough);
      rooted = Math.max(rooted, turn.rootedThrough);
    }
    this.heldThrough = held;
    this.rootedThrough = rooted;
    const error = Math.max(Math.abs(replayed.x - this.state.x), Math.abs(replayed.y - this.state.y));
    this.state = replayed;
    return { error, band: classifyError(error) };
  }
}
